import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { X, Save, UserCircle, ShieldCheck } from "lucide-react";
import { CREATE_USER } from "../apollo/Mutations";
import { GET_ALL_USERS } from "../apollo/Queries";

export default function UserModal({ isOpen, onClose, initialData }) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm();

    const [responseMsg, setResponseMsg] = useState("");
    const [createUser, { loading }] = useMutation(CREATE_USER, {
        refetchQueries: [{ query: GET_ALL_USERS, variables: { page: 0, size: 10 } }]
    });

    useEffect(() => {
        if (initialData) {
            Object.keys(initialData).forEach(key => {
                if (key === 'userType') {
                    setValue('userRole', initialData[key]);
                } else {
                    setValue(key, initialData[key]);
                }
            });
        } else {
            reset();
        }
    }, [initialData, setValue, reset, isOpen]);

    const submitHandler = async (formData) => {
        try {
            const variables = {
                ...formData,
                uuid: initialData?.uuid || ""
            };
            const { data } = await createUser({ variables });

            if (data?.createUser?.error) {
                setResponseMsg(`❌ ${data.createUser.message}`);
            } else {
                setResponseMsg(`✅ ${data.createUser.message}`);
                setTimeout(() => {
                    setResponseMsg("");
                    reset();
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error("GraphQL error:", error);
            setResponseMsg("❌ Failed to save sovereign identity.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-premium-dark/80 backdrop-blur-md p-4">
            <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-fade-in-up">

                {/* Modal Header */}
                <div className="bg-gradient-to-br from-premium-teal to-premium-dark p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-premium-gold/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="relative z-10 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-premium-gold/10 flex items-center justify-center text-premium-gold border border-white/10">
                                <UserCircle size={28} />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-white tracking-tighter">
                                    {initialData ? "Update User" : "Create User"}
                                </h3>
                                <p className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em] opacity-80">Identity Modeler</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white hover:bg-white/10 transition-all border border-white/10"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit(submitHandler)} className="p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-premium-gold ml-1">First Name</label>
                            <input
                                {...register("firstName", { required: "First name is essential" })}
                                placeholder="e.g. Alexander"
                                className="w-full border-gray-100 bg-gray-50/50 p-4 rounded-[1.2rem] focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all font-bold text-premium-teal text-sm"
                            />
                            {errors.firstName && <p className="text-red-500 text-[9px] font-black mt-1 ml-1 uppercase tracking-wider">{errors.firstName.message}</p>}
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-premium-gold ml-1">Last Name</label>
                            <input
                                {...register("lastName", { required: "Last name is essential" })}
                                placeholder="e.g. Great"
                                className="w-full border-gray-100 bg-gray-50/50 p-4 rounded-[1.2rem] focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all font-bold text-premium-teal text-sm"
                            />
                            {errors.lastName && <p className="text-red-500 text-[9px] font-black mt-1 ml-1 uppercase tracking-wider">{errors.lastName.message}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-premium-gold ml-1">Telephone</label>
                            <input
                                {...register("phone", { required: "Phone is essential" })}
                                placeholder="+1 234 567 890"
                                className="w-full border-gray-100 bg-gray-50/50 p-4 rounded-[1.2rem] focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all font-bold text-premium-teal text-sm"
                            />
                            {errors.phone && <p className="text-red-500 text-[9px] font-black mt-1 ml-1 uppercase tracking-wider">{errors.phone.message}</p>}
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-premium-gold ml-1">Username</label>
                            <input
                                {...register("username", { required: "Username is essential" })}
                                placeholder="email Address"
                                className="w-full border-gray-100 bg-gray-50/50 p-4 rounded-[1.2rem] focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all font-bold text-premium-teal text-sm"
                            />
                            {errors.username && <p className="text-red-500 text-[9px] font-black mt-1 ml-1 uppercase tracking-wider">{errors.username.message}</p>}
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-black uppercase tracking-widest text-premium-gold ml-1">User Role</label>
                        <div className="relative">
                            <select
                                {...register("userRole", { required: "Role is essential" })}
                                className="w-full border-gray-100 bg-gray-50/50 p-4 rounded-[1.2rem] focus:ring-2 focus:ring-premium-gold/20 outline-none transition-all font-black text-premium-teal text-xs appearance-none"
                            >
                                <option value="">Select Role</option>
                                <option value="ADMIN">ADMIN</option>
                                <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                                <option value="USER">USER</option>
                                <option value="MANAGER">MANAGER</option>
                                <option value="DELIVERER">DELIVERER</option>
                            </select>
                            <div className="absolute inset-y-0 right-5 flex items-center pointer-events-none text-premium-gold">
                                <ShieldCheck size={18} />
                            </div>
                        </div>
                    </div>

                    <div className="pt-6 flex items-center justify-between border-t border-gray-100/50">
                        <div className="flex-1">
                            {responseMsg && (
                                <p className={`text-[10px] font-black uppercase tracking-widest animate-fade-in ${responseMsg.includes('✅') ? 'text-green-500' : 'text-red-500'}`}>
                                    {responseMsg}
                                </p>
                            )}
                        </div>

                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-8 py-4 bg-gray-50 text-gray-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 transition-all border border-gray-100"
                                disabled={loading}
                            >
                                Abort
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className={`premium-button px-10 py-4 rounded-2xl text-white font-black text-[10px] uppercase tracking-widest shadow-xl transition-all flex items-center gap-3 ${loading ? "bg-premium-teal/50 cursor-not-allowed" : "bg-premium-teal shadow-premium-teal/20"
                                    }`}
                            >
                                {loading ? (
                                    <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                ) : <Save size={16} className="text-premium-gold" />}
                                {loading ? "Syncing..." : initialData ? "Update User" : "Create User"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
